import React, { useState } from "react"
import { useDispatch } from "react-redux"
import * as Yup from "yup"
import { Form, Formik } from "formik"
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import { updateEnterpriseGeneralInfo } from "../../../actions/Organizations/Enterprise"

const validationSchema = Yup.object({
  name: Yup.string().trim().max(100, "100 caractères max").required("Le nom de l'entreprise est requis"),
})

const BOOLEAN_FIELDS = [
  { name: "nationalSendField", label: "Envoi en province" },
  { name: "sendCapitalField", label: "Envoi à Paris" },
  { name: "smallRetailField", label: "Commerce de détail" },
  { name: "lunchbagField", label: "Plateaux repas" },
]

const sx = {
  dialog: {
    "& .MuiDialog-paper": {
      width: 640,
      maxWidth: "calc(100vw - 32px)",
      borderRadius: "8px",
    },
  },
  title: {
    px: 5,
    pt: 3,
    pb: 1,
    fontSize: 18,
    fontWeight: 500,
    pr: 8,
    marginBottom: "20px !important",
  },
  closeBtn: {
    position: "absolute",
    right: 16,
    top: 16,
    color: "text.secondary",
  },
  content: {
    px: 5,
    pt: 1,
    pb: 2,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: 400,
    color: "#757575",
    mb: 1,
  },
  codeInput: {
    "& .MuiInput-root": { fontSize: 14 },
    "& .MuiInput-underline:before": { borderBottomStyle: "dashed", borderBottomColor: "#E0E0E0" },
    "& .MuiInput-underline.Mui-disabled:before": { borderBottomStyle: "dashed" },
  },
  serverError: {
    px: 5,
    pb: 1,
    fontSize: 14,
    color: "#B71C1C",
  },
  actions: {
    px: 5,
    pt: 3,
    pb: 4,
    justifyContent: "space-between",
    borderTop: "1px solid",
    borderColor: "divider",
  },
}

const EditGeneralInfoModal = ({ open, onClose, enterprise }) => {
  const dispatch = useDispatch()
  const [serverError, setServerError] = useState(null)

  const initialValues = {
    name: enterprise.name || "",
    requiresValidation: Boolean(enterprise.requiresValidation),
    nationalSendField: enterprise.nationalSendField == null ? null : Boolean(enterprise.nationalSendField),
    sendCapitalField: enterprise.sendCapitalField == null ? null : Boolean(enterprise.sendCapitalField),
    smallRetailField: enterprise.smallRetailField == null ? null : Boolean(enterprise.smallRetailField),
    lunchbagField: enterprise.lunchbagField == null ? null : Boolean(enterprise.lunchbagField),
  }

  const handleSubmit = async (values, { setSubmitting }) => {
    setServerError(null)
    try {
      await dispatch(updateEnterpriseGeneralInfo(enterprise.objectId, {
        name: values.name.trim(),
        requiresValidation: values.requiresValidation,
        nationalSendField: values.nationalSendField,
        sendCapitalField: values.sendCapitalField,
        smallRetailField: values.smallRetailField,
        lunchbagField: values.lunchbagField,
      }))
      onClose()
    } catch (e) {
      setServerError("Une erreur est survenue, veuillez réessayer")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} sx={sx.dialog}>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, handleChange, setFieldValue, isSubmitting, dirty, isValid }) => (
          <Form>
            <DialogTitle sx={sx.title}>
              Modifier les informations générales
              <IconButton onClick={onClose} disabled={isSubmitting} sx={sx.closeBtn}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={sx.content}>
              <Stack spacing={3}>
                <Box>
                  <Typography sx={sx.fieldLabel}>Nom de l&apos;entreprise</Typography>
                  <TextField
                    fullWidth
                    variant="standard"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    inputProps={{ maxLength: 100 }}
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                  />
                </Box>

                <Box>
                  <Typography sx={sx.fieldLabel}>Code</Typography>
                  <TextField
                    fullWidth
                    variant="standard"
                    value={enterprise.code || ""}
                    disabled
                    sx={sx.codeInput}
                  />
                </Box>

                <Stack>
                  {BOOLEAN_FIELDS.map(({ name, label }) => (
                    <FormControlLabel
                      key={name}
                      control={
                        <Checkbox
                          checked={!!values[name]}
                          onChange={(e) => setFieldValue(name, e.target.checked)}
                        />
                      }
                      label={label}
                    />
                  ))}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.requiresValidation}
                        onChange={(e) => setFieldValue("requiresValidation", e.target.checked)}
                      />
                    }
                    label="Validation client requise"
                  />
                </Stack>
                </Stack>
            </DialogContent>

            {serverError && (
              <Typography sx={sx.serverError}>{serverError}</Typography>
            )}

            <DialogActions sx={sx.actions}>
              <Button variant="text" onClick={onClose} disabled={isSubmitting}>
                Annuler
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting || !dirty || !isValid}
                startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {isSubmitting ? "Enregistrement…" : serverError ? "Réessayer" : "Enregistrer"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}

export default EditGeneralInfoModal
